/**
 * Задание stylepack/scale-labels (слово владельца 21.10.2026: повторять чужую
 * воронку в точности). У Calm под шкалой из пяти эмодзи в ряд — ОДНОЙ строкой
 * слева «Strongly disagree», справа «Strongly agree», мелким серым.
 *
 * ЗЕРКАЛО канона сервера `backend/src/ai-funnels/block-schema/scale-labels.ts`:
 * перечни и числа ниже читает с диска сторож `scale-labels.ends.spec.ts` и
 * сверяет со схемой, панелью и отрисовкой в обе стороны — форму
 * `[...] as const`, `= N;` и `{ min: N, max: N } as const` не менять, не
 * обновив его.
 *
 * Строка подписей рисуется ПОД списком вариантов (PublicBlockRenderer.tsx,
 * ветки single_choice и multiple_choice). Нет ни одной подписи — не рисуется
 * ничего: разметка старой воронки та же байт в байт.
 */
import React from 'react';

export const SCALE_LABEL_FIELDS = [
  'optionScaleStartLabel',
  'optionScaleEndLabel',
  'optionScaleLabelColor',
  'optionScaleLabelFontSize',
] as const;

export const SCALE_LABEL_BLOCK_TYPES = [
  'single_choice',
  'multiple_choice',
] as const;

export const SCALE_LABEL_MAX_LENGTH = 60;

export const SCALE_LABEL_FONT_SIZE_BOUNDS = { min: 8, max: 24 } as const;

export const SCALE_LABEL_FONT_SIZE_DEFAULT = 12;

/** Нет своего цвета — вторичный текст темы, как у подписей краёв Лайкерта. */
export const SCALE_LABEL_DEFAULT_COLOR = 'var(--color-text-secondary, #8c8c8c)';

/**
 * Код цвета — та же форма, что у схемы сервера (BLOCK_COLOR_PATTERN,
 * color-fields.ts; текст образца сверяет сторож концов). Своей копией, а не
 * импортом из BlockColorField.tsx: тот тянет в публичную воронку antd.
 */
export const SCALE_LABEL_COLOR_PATTERN =
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Поля, которые читает строка подписей. */
export interface ScaleLabelFieldsValues {
  optionScaleStartLabel?: string;
  optionScaleEndLabel?: string;
  optionScaleLabelColor?: string;
  optionScaleLabelFontSize?: number;
}

/** Есть ли подписи шкалы у типа блока (собственный ключ, не прототип). */
export function hasScaleLabels(type: string): boolean {
  return (SCALE_LABEL_BLOCK_TYPES as readonly string[]).includes(type);
}

const textOf = (raw: unknown): string =>
  typeof raw === 'string' ? raw.trim() : '';

/** Подписи для отрисовки: не строка или одни пробелы — пустая подпись. */
export function scaleLabelsOf(fields: ScaleLabelFieldsValues): {
  start: string;
  end: string;
} {
  return {
    start: textOf(fields.optionScaleStartLabel),
    end: textOf(fields.optionScaleEndLabel),
  };
}

/** Кегль: целое в границах сервера, иначе 12. */
export function scaleLabelFontSizeOf(fields: ScaleLabelFieldsValues): number {
  const raw: unknown = fields.optionScaleLabelFontSize;
  return typeof raw === 'number' &&
    Number.isInteger(raw) &&
    raw >= SCALE_LABEL_FONT_SIZE_BOUNDS.min &&
    raw <= SCALE_LABEL_FONT_SIZE_BOUNDS.max
    ? raw
    : SCALE_LABEL_FONT_SIZE_DEFAULT;
}

/** Цвет: свой код цвета, иначе вторичный текст темы. */
export function scaleLabelColorOf(fields: ScaleLabelFieldsValues): string {
  const raw: unknown = fields.optionScaleLabelColor;
  return typeof raw === 'string' && SCALE_LABEL_COLOR_PATTERN.test(raw)
    ? raw
    : SCALE_LABEL_DEFAULT_COLOR;
}

/**
 * Половина строки: делит ширину поровну с соседней и может сжаться уже
 * своего текста — длинная подпись переносится ВНУТРИ своей половины, а не
 * уводит строку в столбик (на телефоне 360 px то же, что на компьютере).
 */
const HALF: React.CSSProperties = {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  minWidth: 0,
  overflowWrap: 'anywhere',
};

/**
 * Строка подписей концов шкалы: начало у левого края, конец у правого.
 * Обе половины рисуются всегда — одна подпись конца всё равно стоит справа.
 */
export function OptionScaleLabels({
  fields,
}: {
  fields: ScaleLabelFieldsValues;
}): React.ReactElement | null {
  const { start, end } = scaleLabelsOf(fields);
  if (!start && !end) return null;
  return (
    <div
      data-testid="option-scale-labels"
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8,
        fontSize: scaleLabelFontSizeOf(fields),
        lineHeight: 1.3,
        color: scaleLabelColorOf(fields),
      }}
    >
      <span style={{ ...HALF, textAlign: 'left' }}>{start}</span>
      <span style={{ ...HALF, textAlign: 'right' }}>{end}</span>
    </div>
  );
}
